import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';

const { UUID } = sdkTypes;

// ===============================================================
// Action types
// ===============================================================

const FETCH_LISTING_REQUEST = 'app/DynamicPricingPage/FETCH_LISTING_REQUEST';
const FETCH_LISTING_SUCCESS = 'app/DynamicPricingPage/FETCH_LISTING_SUCCESS';
const FETCH_LISTING_ERROR = 'app/DynamicPricingPage/FETCH_LISTING_ERROR';

const ACCEPT_PRICING_REQUEST = 'app/DynamicPricingPage/ACCEPT_PRICING_REQUEST';
const ACCEPT_PRICING_SUCCESS = 'app/DynamicPricingPage/ACCEPT_PRICING_SUCCESS';
const ACCEPT_PRICING_ERROR = 'app/DynamicPricingPage/ACCEPT_PRICING_ERROR';

const SEND_MESSAGE_REQUEST = 'app/DynamicPricingPage/SEND_MESSAGE_REQUEST';
const SEND_MESSAGE_SUCCESS = 'app/DynamicPricingPage/SEND_MESSAGE_SUCCESS';
const SEND_MESSAGE_ERROR = 'app/DynamicPricingPage/SEND_MESSAGE_ERROR';

const MARK_READ_REQUEST = 'app/DynamicPricingPage/MARK_READ_REQUEST';
const MARK_READ_SUCCESS = 'app/DynamicPricingPage/MARK_READ_SUCCESS';

// ===============================================================
// Reducer
// ===============================================================

const initialState = {
  fetchInProgress: false,
  fetchError: null,
  listing: null,
  acceptInProgress: false,
  acceptError: null,
  sendMessageInProgress: false,
  sendMessageError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTING_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_LISTING_SUCCESS:
      return { ...state, fetchInProgress: false, listing: payload };
    case FETCH_LISTING_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };

    case ACCEPT_PRICING_REQUEST:
      return { ...state, acceptInProgress: true, acceptError: null };
    case ACCEPT_PRICING_SUCCESS:
      return { ...state, acceptInProgress: false };
    case ACCEPT_PRICING_ERROR:
      return { ...state, acceptInProgress: false, acceptError: payload };

    case SEND_MESSAGE_REQUEST:
      return { ...state, sendMessageInProgress: true, sendMessageError: null };
    case SEND_MESSAGE_SUCCESS:
      return { ...state, sendMessageInProgress: false, listing: payload };
    case SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false, sendMessageError: payload };

    case MARK_READ_REQUEST:
      return state;
    case MARK_READ_SUCCESS:
      return state;

    default:
      return state;
  }
}

// ===============================================================
// Action creators
// ===============================================================

const fetchListingRequest = () => ({ type: FETCH_LISTING_REQUEST });
const fetchListingSuccess = listing => ({ type: FETCH_LISTING_SUCCESS, payload: listing });
const fetchListingError = e => ({ type: FETCH_LISTING_ERROR, payload: e, error: true });

const acceptPricingRequest = () => ({ type: ACCEPT_PRICING_REQUEST });
const acceptPricingSuccess = () => ({ type: ACCEPT_PRICING_SUCCESS });
const acceptPricingError = e => ({ type: ACCEPT_PRICING_ERROR, payload: e, error: true });

const sendMessageRequest = () => ({ type: SEND_MESSAGE_REQUEST });
const sendMessageSuccess = listing => ({ type: SEND_MESSAGE_SUCCESS, payload: listing });
const sendMessageError = e => ({ type: SEND_MESSAGE_ERROR, payload: e, error: true });

const markReadRequest = () => ({ type: MARK_READ_REQUEST });
const markReadSuccess = listing => ({ type: MARK_READ_SUCCESS, payload: listing });

// ===============================================================
// Thunks
// ===============================================================

export const fetchListing = id => (dispatch, getState, sdk) => {
  dispatch(fetchListingRequest());
  return sdk.ownListings
    .show({ id: new UUID(id) })
    .then(response => {
      dispatch(fetchListingSuccess(response.data.data));
      return response;
    })
    .catch(e => {
      dispatch(fetchListingError(storableError(e)));
      throw e;
    });
};

export const acceptDynamicPricing = (id, values) => (dispatch, getState, sdk) => {
  dispatch(acceptPricingRequest());
  return sdk.ownListings
    .update({ id: new UUID(id), ...values }, { expand: true })
    .then(() => {
      dispatch(acceptPricingSuccess());
    })
    .catch(e => {
      dispatch(acceptPricingError(storableError(e)));
      throw e;
    });
};

export const sendMessage = (id, message, existingSellerMessages) => (dispatch, getState, sdk) => {
  dispatch(sendMessageRequest());

  const existingMessages = existingSellerMessages?.messages || [];
  const newMessage = {
    author: 'seller',
    message,
    subject: 'Pricing',
    timestamp: new Date().toISOString(),
  };
  const updatedMessages = [...existingMessages, newMessage];

  return sdk.ownListings
    .update(
      {
        id: new UUID(id),
        privateData: {
          sellerMessages: {
            messages: updatedMessages,
            readAt: existingSellerMessages?.readAt || null,
          },
        },
      },
      { expand: true }
    )
    .then(response => {
      dispatch(sendMessageSuccess(response.data.data));
      return response;
    })
    .catch(e => {
      dispatch(sendMessageError(storableError(e)));
      throw e;
    });
};

export const markMessagesRead = (id, existingSellerMessages) => (dispatch, getState, sdk) => {
  dispatch(markReadRequest());
  return sdk.ownListings
    .update(
      {
        id: new UUID(id),
        privateData: {
          sellerMessages: {
            messages: existingSellerMessages?.messages || [],
            readAt: new Date().toISOString(),
          },
        },
      },
      { expand: true }
    )
    .then(response => {
      dispatch(markReadSuccess(response.data.data));
    })
    .catch(() => {});
};

export const loadData = params => (dispatch, getState, sdk) => {
  const { id } = params;
  return Promise.all([
    dispatch(fetchListing(id)),
    dispatch(fetchCurrentUser()),
  ]);
};
